import React, { MouseEventHandler, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useAttributes } from '@hozana/dom/hooks/useAttributes'
import { email, required } from '@hozana/form/validate'
import { usePrevious } from '@hozana/hooks/usePrevious'
import { useDispatch } from '@hozana/redux/hooks'
import { bindRoutineToHookForm } from '@hozana/routines/functions'
import { QA } from '@hozana/tests/constants'

import { Button } from 'elements/button/Button'
import { Form } from 'elements/input/Form'
import { FormRootError } from 'elements/input/FormRootError'
import { Input } from 'elements/input/Input'
import { InputGroup } from 'elements/input/InputGroup'
import { Textarea } from 'elements/input/Textarea'
import { Cell } from 'elements/layout/Cell'
import { Row } from 'elements/layout/Row'
import { Text } from 'elements/text/Text'

import { MAX_INTENTION_LENGTH } from 'modules/intention/constants'
import { useIntentionsWidget } from 'modules/intention/hooks/useIntentionsWidget'
import { useMyIntention } from 'modules/intention/hooks/useMyIntention'
import { editIntention, postIntention } from 'modules/intention/routines'

const AnimatedTextarea = styled(Textarea)`
  transition: all 0.3s ease-in-out;
`

export type TIntentionFormFormData = {
  guestName: string
  email: string
  content: string
}

export type TIntentionFormProps = {
  isExpired?: boolean
  landingPageSlug?: string
  onSubmitSuccess?: (values: TIntentionFormFormData) => void | Promise<void>
  textareaHeight?: string
  onCancel?: MouseEventHandler<HTMLButtonElement>
  charsLeft?: boolean
  placeholder?: string
  publishButtonText?: string
  successFlashMessage?: string
  shouldHideActionsWhenEmpty?: boolean
}

export const IntentionForm: TFC<TIntentionFormProps> = ({
  isExpired,
  landingPageSlug,
  onSubmitSuccess,
  textareaHeight = '90px',
  onCancel,
  charsLeft,
  publishButtonText,
  successFlashMessage,
  shouldHideActionsWhenEmpty = false,
}) => {
  const attrs = useAttributes()
  const [isFocused, setIsFocused] = useState(false)
  const prevIsExpired = usePrevious(isExpired)
  const {
    i18n: { language },
    t,
  } = useTranslation()
  const dispatch = useDispatch()
  const myIntention = useMyIntention()
  const widget = useIntentionsWidget()
  const useFormOptions = myIntention
    ? {
        values: {
          content: myIntention.content,
          ...(myIntention.guestName && { guestName: myIntention.guestName }),
          ...(myIntention.email && { email: myIntention.email }),
        },
      }
    : {}
  const form = useForm<TIntentionFormFormData>(useFormOptions)
  const { formState, handleSubmit, setError, watch, unregister } = form

  useEffect(() => {
    if (isExpired && !prevIsExpired) {
      unregister()
    }
  }, [isExpired, prevIsExpired, unregister])

  const onSubmit: SubmitHandler<TIntentionFormFormData> = async (data) => {
    try {
      if (myIntention) {
        await bindRoutineToHookForm(editIntention, {
          dispatch,
          setError,
          onSuccess: () => onSubmitSuccess(data),
          additionalProps: { widget, intentionId: myIntention.id, flashMessage: successFlashMessage },
        })(data)
      } else {
        await bindRoutineToHookForm(postIntention, {
          dispatch,
          setError,
          onSuccess: () => onSubmitSuccess(data),
          additionalProps: {
            landingPageSlug,
            widget,
            locale: language,
            flashMessage: successFlashMessage,
          },
        })(data)
      }
    } catch (error) {
      /* Empty */
    }
  }

  const shouldHideButtons = shouldHideActionsWhenEmpty && !watch('content')

  return (
    <Form id="IntentionForm" form={form} onSubmit={handleSubmit(onSubmit)} data-testid={QA.INTENTION.FORM}>
      <Row m="0 0 10px 0">
        <Cell flex="1">
          {widget && (
            <>
              <InputGroup name="guestName" m="0 0 10px">
                <Input
                  placeholder="intention:word.your-firstname"
                  borderRadius="10px"
                  data-testid={QA.INTENTION.NAME_INPUT}
                  noBorder
                  bg="background"
                  p="5px 10px"
                  fontSize="1em"
                  {...attrs}
                />
              </InputGroup>
              <InputGroup name="email" options={[required(), email()]} m="0 0 10px">
                <Input
                  type="email"
                  placeholder="intention:word.email-address"
                  noBorder
                  bg="background"
                  p="5px 10px"
                  fontSize="1em"
                  data-testid={QA.INTENTION.EMAIL_INPUT}
                  borderRadius="10px"
                  {...attrs}
                />
              </InputGroup>
            </>
          )}
          <InputGroup name="content" options={required()}>
            <AnimatedTextarea
              placeholder={
                widget
                  ? 'intention:intention.widget.form.textarea.placeholder'
                  : 'intention:intention.form.textarea.placeholder'
              }
              noBorder
              maxLength={MAX_INTENTION_LENGTH}
              h={isFocused || formState.isDirty ? '150px' : textareaHeight}
              resize="none"
              bg="background"
              p="10px 1em"
              borderRadius="medium"
              fontSize="1em"
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              data-testid={QA.INTENTION.CONTENT_INPUT}
              {...attrs}
            />
          </InputGroup>
        </Cell>
      </Row>
      <FormRootError form={form} m="10px" />
      {!shouldHideButtons && (
        <Row align="middleBetween" gutter="7px">
          {charsLeft && (
            <Cell flex="1" align="middleLeft">
              <Text color="black50" fontSize="0.9em">
                {t('common:word.characters', { count: MAX_INTENTION_LENGTH - (watch('content')?.length || 0) })}
              </Text>
            </Cell>
          )}
          {myIntention && onCancel && (
            <Cell m="0 10px 0 0" flex="1">
              <Button type="button" colors="greyLight" onClick={onCancel} {...attrs}>
                trans:intention:word.cancel
              </Button>
            </Cell>
          )}
          <Cell flex="1">
            <Button
              data-testid={QA.INTENTION.PUBLISH_BUTTON}
              disabled={formState.isSubmitting}
              type="submit"
              w="100%"
              colors="yellow"
              {...attrs}
            >
              {publishButtonText || 'trans:intention:word.publish'}
            </Button>
          </Cell>
        </Row>
      )}
    </Form>
  )
}
